import React, { useState } from 'react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GraduationCap, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MortgageData } from '@/types/calculator';
import { LiveRatesCard } from './LiveRatesCard';
import { TexasPropertyTaxCard } from './TexasPropertyTaxCard';
import { RefinanceComparisonCard } from './RefinanceComparisonCard';
import { LandUseRestrictionsCard } from './LandUseRestrictionsCard';
import { motion, AnimatePresence } from 'framer-motion';

interface EducationalPanelProps {
  mortgageData: MortgageData;
}

export const EducationalPanel: React.FC<EducationalPanelProps> = ({ mortgageData }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
      <CardHeader className="cursor-pointer py-3 md:py-6" onClick={() => setIsExpanded(!isExpanded)}>
        <CardTitle className="flex items-center gap-2 text-sm md:text-lg">
          <GraduationCap className="w-4 h-4 md:w-5 md:h-5 text-primary" />
          <span className="hidden sm:inline">Mortgage Education Center</span>
          <span className="sm:hidden">Learn More</span>
          <span className="ml-auto text-xs md:text-sm text-muted-foreground flex items-center gap-1 md:gap-2">
            <span className="hidden md:inline">Real-time data & expert insights</span>
            {isExpanded ? <ChevronUp className="w-3 h-3 md:w-4 md:h-4" /> : <ChevronDown className="w-3 h-3 md:w-4 md:h-4" />}
          </span>
        </CardTitle>
      </CardHeader>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <CardContent className="pt-0 px-3 md:px-6 pb-3 md:pb-6">
              <Carousel className="w-full" opts={{ loop: true, align: "start" }}>
                <CarouselContent>
                  <CarouselItem>
                    <LiveRatesCard />
                  </CarouselItem>
                  <CarouselItem>
                    <TexasPropertyTaxCard mortgageData={mortgageData} />
                  </CarouselItem>
                  <CarouselItem>
                    <RefinanceComparisonCard />
                  </CarouselItem>
                  <CarouselItem>
                    <LandUseRestrictionsCard />
                  </CarouselItem>
                </CarouselContent>
                <div className="flex items-center justify-center gap-3 md:gap-4 mt-3 md:mt-4">
                  <CarouselPrevious className="static translate-y-0 h-9 w-9 md:h-8 md:w-8" />
                  <CarouselNext className="static translate-y-0 h-9 w-9 md:h-8 md:w-8" />
                </div>
              </Carousel>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
};
